<template>
  <div
    ref="headerRef"
    :style="styles"
    :class="[
      'sticky top-0 z-50 transition-shadow duration-200',
      { 'shadow-md': isFixed }
    ]"
  >
    <nav class="bg-background border-b">
      <div class="px-4">
        <div class="flex h-16 items-center justify-between">
          <!-- Logo and site name -->
          <div>
            <NuxtLink class="flex items-center gap-2.5" to="/">
              <img
                :src="siteStore?.logo || '/logo.png'"
                :alt="siteStore?.name || ''"
                class="h-5 w-auto mr-4"
                loading="eager"
                decoding="async"
                fetchpriority="high"
              />
              <span class="text-lg font-semibold md:block hidden">{{ siteStore?.name || '' }}</span>
              <span class="text-base font-semibold md:hidden">{{ siteStore?.name || '' }}</span>
            </NuxtLink>
          </div>

          <!-- Header actions -->
          <div class="flex items-center gap-2 md:gap-3">
            <!-- Cart Button -->
            <button
              @click="toggleCart"
              class="p-2 rounded-md hover:bg-accent/50 active:bg-accent transition-colors relative"
              aria-label="View cart"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <span
                v-if="cartCount > 0"
                class="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-sm"
              >
                {{ cartCount < 10 ? cartCount : '9+' }}
              </span>
            </button>

            <!-- Favorites Link -->
            <NuxtLink
              to="/favorites"
              class="p-2 rounded-md hover:bg-accent/50 active:bg-accent transition-colors relative"
              aria-label="View favorites"
            >
              <HeartIcon class="h-5 w-5 text-foreground" />
              <span
                v-if="favoritesCount > 0"
                class="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-sm"
              >
                {{ favoritesCount < 10 ? favoritesCount : '9+' }}
              </span>
            </NuxtLink>

            <!-- Dark Mode Toggle -->
            <button
              @click="toggleDarkMode"
              class="p-2 rounded-md hover:bg-accent/50 active:bg-accent transition-colors"
              aria-label="Toggle dark mode"
            >
              <ClientOnly>
                <SunIcon v-if="isDarkMode" class="h-5 w-5 text-foreground" />
                <MoonIcon v-else class="h-5 w-5 text-foreground" />
                <template #fallback>
                  <span class="inline-block h-5 w-5"></span>
                </template>
              </ClientOnly>
            </button>

            <!-- Phone number - hidden on mobile, visible on larger screens -->
            <a
              v-if="siteStore?.phone"
              :href="`tel:${siteStore.phone}`"
              class="hidden md:block text-sm font-medium text-foreground/80 hover:text-foreground"
            >
              {{ siteStore.phone }}
            </a>
            <!-- Mobile phone icon -->
            <a
              v-if="siteStore?.phone"
              :href="`tel:${siteStore.phone}`"
              class="md:hidden p-2 rounded-md hover:bg-accent/50 active:bg-accent transition-colors"
              aria-label="Call us"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </a>

            <!-- Mobile menu button -->
            <div class="overflow-hidden">
              <Sheet v-model:open="mobileOpen">
                <SheetTrigger as-child>
                  <button 
                    type="button" 
                    class="inline-flex h-10 w-10 items-center justify-center rounded-md hover:bg-accent/50 active:bg-accent transition-colors text-foreground lg:hidden" 
                    aria-label="Open menu"
                  >
                    <Bars3Icon class="h-6 w-6" aria-hidden="true" />
                  </button>
                </SheetTrigger>

                <!-- Mobile menu content -->
                <SheetContent side="left" class="w-[100vw] max-w-[600px] p-0 mobile-sheet-content">
                  <div class="flex flex-col h-full">
                    <SheetHeader class="flex items-center justify-between px-4 py-5 border-b flex-shrink-0">
                      <div class="flex items-center">
                        <SheetTitle class="flex items-center">
                          <img 
                            :src="siteStore?.logo || '/logo.png'" 
                            :alt="siteStore?.name || 'Site Logo'"
                            class="h-6 w-auto"
                            loading="eager"
                            decoding="async"
                            fetchpriority="high"
                          />
                        </SheetTitle>
                        <SheetDescription class="sr-only">
                          Mobile navigation menu with vehicle filters and site navigation
                        </SheetDescription>
                      </div>
                    </SheetHeader>

                    <div class="flex-1 overflow-y-auto sheet-scrollable">
                      <div class="flex flex-col">
                    <!-- Mobile menu actions -->
                    <div class="flex items-center justify-between gap-4 px-4 py-3 border-b">
                      <!-- Dark Mode Toggle -->
                      <button 
                        @click="toggleDarkMode"
                        class="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-accent/50 active:bg-accent transition-colors text-sm font-medium"
                      >
                        <ClientOnly>
                          <SunIcon v-if="isDarkMode" class="h-5 w-5" />
                          <MoonIcon v-else class="h-5 w-5" />
                          <template #fallback>
                            <span class="inline-block h-5 w-5"></span>
                          </template>
                        </ClientOnly>
                        <span>{{ isDarkMode ? 'Light Mode' : 'Dark Mode' }}</span>
                      </button>
                      
                      <!-- Contact Button -->
                      <NuxtLink 
                        to="/contact" 
                        class="flex items-center gap-2 px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
                        @click="mobileOpen = false"
                      >
                        <span>Contact Us</span>
                      </NuxtLink>
                    </div>

                    <!-- Mobile menu links -->
                    <div class="mt-2 overflow-hidden">
                      <!-- Filters Accordion (New) -->
                      <div class="mobile-nav-item">
                        <Disclosure v-slot="{ open }">
                          <DisclosureButton 
                            class="flex w-full items-center justify-between px-5 py-4 font-light text-2xl ml-2 hover:text-primary text-foreground"
                          >
                            <span>Filter Vehicles</span>
                            <ArrowRightIcon 
                              class="h-5 w-5 flex-none text-muted-foreground transition-transform duration-200" 
                              :class="{ 'rotate-90': open }"
                              aria-hidden="true" 
                            />
                          </DisclosureButton>
                          <DisclosurePanel class="px-10 py-3 bg-muted/30">
                            <MobileFiltersContent @close-menu="mobileOpen = false" />
                          </DisclosurePanel>
                        </Disclosure>
                      </div>

                      <div v-for="(link, index) in navigationLinks" :key="index" class="mobile-nav-item">
                        <!-- Models Mega Menu (Mobile) -->
                        <Disclosure v-if="link.name === 'Models'" v-slot="{ open }">
                          <DisclosureButton 
                            class="flex w-full items-center justify-between px-5 py-4 font-light text-2xl ml-2 hover:text-primary text-foreground"
                          >
                            <span>{{ link.name }}</span>
                            <ArrowRightIcon 
                              class="h-5 w-5 flex-none text-muted-foreground transition-transform duration-200" 
                              :class="{ 'rotate-90': open }"
                              aria-hidden="true" 
                            />
                          </DisclosureButton>
                          <DisclosurePanel class="px-2 py-3 bg-muted/30">
                            <MobileVehicleModels />
                          </DisclosurePanel>
                        </Disclosure>

                        <!-- Vehicles Link (Mobile) -->
                        <NuxtLink 
                          v-else-if="link.name === 'Vehicles'"
                          :to="{ path: link.url, query: currentFilterQuery }"
                          class="flex items-center px-5 py-4 font-light text-2xl ml-2 hover:text-primary text-foreground active:bg-accent/50"
                          @click="mobileOpen = false"
                        >
                          {{ link.name }}
                        </NuxtLink>

                        <!-- Link with submenu (accordion) -->
                        <Disclosure v-else-if="link.submenu?.rendered" v-slot="{ open }">
                          <DisclosureButton 
                            class="flex w-full items-center justify-between px-5 py-4 text-base font-medium text-foreground"
                          >
                            <span>{{ link.name }}</span>
                            <ArrowRightIcon 
                              class="h-5 w-5 flex-none text-muted-foreground transition-transform duration-200" 
                              :class="{ 'rotate-90': open }"
                              aria-hidden="true" 
                            />
                          </DisclosureButton>
                          <DisclosurePanel class="px-5 py-3 bg-muted/30">
                            <div 
                              class="mobile-submenu-content" 
                              v-html="processedSubmenu(link.submenu.rendered)"
                              @click="handleMobileSubmenuClick"
                            ></div>
                          </DisclosurePanel>
                        </Disclosure>

                        <!-- Regular link -->
                        <NuxtLink 
                          v-else
                          :to="link.url"
                          class="flex items-center px-5 py-4 font-light text-2xl ml-2 hover:text-primary text-foreground active:bg-accent/50"
                          @click="mobileOpen = false"
                        >
                          {{ link.name }}
                        </NuxtLink>
                      </div>
                    </div>

                    <!-- Phone number and social media -->
                    <div class="border-t border-border pt-4 px-5 pb-6">
                      <a 
                        v-if="siteStore?.phone"
                        :href="`tel:${siteStore.phone}`"
                        class="flex items-center gap-3 py-3 text-foreground"
                      >
                        <div class="flex-shrink-0 h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                        </div>
                        <div>
                          <div class="text-sm font-medium">Call us</div>
                          <div class="text-base font-semibold">{{ siteStore.phone }}</div>
                        </div>
                      </a>
                      
                      <!-- Social media links -->
                      <div class="flex items-center gap-4 mt-4 py-3">
                        <a 
                          v-if="siteStore?.social?.facebook"
                          :href="siteStore.social.facebook"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="h-10 w-10 rounded-full bg-accent/50 flex items-center justify-center text-foreground hover:bg-accent transition-colors"
                          aria-label="Facebook"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-facebook"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path></svg>
                        </a>
                        <a 
                          v-if="siteStore?.social?.twitter"
                          :href="siteStore.social.twitter"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="h-10 w-10 rounded-full bg-accent/50 flex items-center justify-center text-foreground hover:bg-accent transition-colors"
                          aria-label="Twitter"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-twitter"><path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"></path></svg>
                        </a>
                        <a 
                          v-if="siteStore?.social?.instagram"
                          :href="siteStore.social.instagram"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="h-10 w-10 rounded-full bg-accent/50 flex items-center justify-center text-foreground hover:bg-accent transition-colors"
                          aria-label="Instagram"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-instagram"><rect width="20" height="20" x="2" y="2" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" x2="17.51" y1="6.5" y2="6.5"></line></svg>
                        </a>
                      </div>
                    </div>
                      </div>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>
    </nav>
    

    
    <!-- Main Desktop Navigation -->
    <TheNavigation />
    
    <!-- Add NavSearchFilters component -->
    <NavSearchFilters />

    <!-- Cart Dialog -->
    <CartDialog
      :is-open="isCartOpen"
      @close="closeCart"
    />


  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useFixedHeader } from 'vue-use-fixed-header'
import { useSiteStore } from '~/stores/site'
import { useFavoritesStore } from '~/stores/favorites'
import { useCartStore } from '~/stores/cartStore'
import { useModelsStore } from '~/stores/models'
import { useVehiclesStore } from '~/stores/vehicles'
import { storeToRefs } from 'pinia'
import { SunIcon, MoonIcon, HeartIcon, Bars3Icon, ArrowRightIcon } from '@heroicons/vue/24/outline'
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger, SheetClose } from '~/layers/base/components/ui/sheet'
import MobileVehicleModels from '~/layers/base/components/MobileVehicleModels.vue'
import MobileFiltersContent from '~/layers/base/components/MobileFiltersContent.vue'
import CartDialog from '~/layers/base/components/cart/CartDialog.vue'
import { useBodyClass } from '~/layers/base/composables/useBodyClass'
import { useRouter } from 'vue-router'
import { useNuxtApp, navigateTo } from 'nuxt/app'

// Define interface for navigation links
interface NavigationLink {
  name: string;
  url: string;
  submenu?: {
    rendered: string;
  };
}

interface SiteStoreWithSocial {
  logo?: string;
  name?: string;
  phone?: string;
  social?: {
    facebook?: string;
    twitter?: string;
    instagram?: string;
    [key: string]: string | undefined;
  };
  navigation?: NavigationLink[];
  initialize?: () => Promise<void>;
}

const siteStore = useSiteStore() as unknown as SiteStoreWithSocial
const favoritesStore = useFavoritesStore()
const cartStore = useCartStore()
const modelsStore = useModelsStore()
const vehiclesStore = useVehiclesStore()
const router = useRouter()
const { $colorMode: colorMode } = useNuxtApp()

const { cartCount, isCartOpen } = storeToRefs(cartStore)

const favoritesCount = computed(() => favoritesStore.favoriteCount);

const headerRef = ref(null)
const { styles, isLeave: isFixed } = useFixedHeader(headerRef)
const isLoading = ref(true)
const mobileOpen = ref(false)

// Define a prop for the sticky class name with a default value
const props = defineProps({
  stickyBodyClass: {
    type: String,
    default: 'header-is-sticky'
  }
})

// Manage both sticky and not-sticky body classes
watch(isFixed, (newValue) => {
  if (typeof document !== 'undefined') {
    
    if (newValue) {
      // Header is sticky
      document.body.classList.add(props.stickyBodyClass)
      document.body.classList.remove('header-not-sticky')
    } else {
      // Header is not sticky
      document.body.classList.remove(props.stickyBodyClass)
      document.body.classList.add('header-not-sticky')
    }
    
  }
}, { immediate: true })

// Set initial state for not-sticky
onMounted(() => {
  if (typeof document !== 'undefined') {
    if (!isFixed.value) {
      document.body.classList.add('header-not-sticky')
    }
  }
})

const isDarkMode = computed(() => colorMode.value === 'dark')

const toggleDarkMode = () => {
  colorMode.preference = colorMode.value === 'dark' ? 'light' : 'dark'
}

// No local mount gate needed; icons wrapped in ClientOnly

// Interface for range filter values
interface RangeFilterValue {
  min: number;
  max: number;
  type: 'range';
}

function isRangeFilter(value: unknown): value is RangeFilterValue {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as any).type === 'range' &&
    typeof (value as any).min === 'number' &&
    typeof (value as any).max === 'number'
  )
}

// Computed property for current filter query
const currentFilterQuery = computed(() => {
  const query: Record<string, string | string[]> = {}
  
  // Add search query if present
  if (vehiclesStore.activeFilters.search_keywords) {
    const searchKeywords = vehiclesStore.activeFilters.search_keywords
    if (searchKeywords.type === 'text') {
      query.search_keywords = searchKeywords.value
    }
  }
  
  // Add other active filters
  Object.entries(vehiclesStore.activeFilters).forEach(([key, value]) => {
    if (value) {
      if (Array.isArray(value) && value.length > 0) {
        query[key] = value.join(',')
      } else if (key === 'perweek' && isRangeFilter(value)) {
        // For range filters like budget, add as separate min/max parameters
        const rangeValue = value
        query[`${key}_min`] = String(rangeValue.min)
        query[`${key}_max`] = String(rangeValue.max)
      }
    }
  })
  
  return query
})

// Navigation links
const navigationLinks = computed<NavigationLink[]>(() => {
  const links: NavigationLink[] = [
    { name: 'Home', url: '/' },
    { name: 'Models', url: '/models' },
    { name: 'Vehicles', url: '/vehicles' },
    { name: 'Cars for Finance', url: '/cars-for-finance' },
    { name: 'Cars For Sale', url: '/cars-for-sale' },
    { name: 'Services', url: '/services' },
    { name: 'About', url: '/about' },
    { name: 'Contact', url: '/contact' }
  ]
  
  // Add custom links from site configuration if available
  const siteNavigation = siteStore.navigation
  if (siteNavigation?.length) {
    return siteNavigation.map((link: NavigationLink) => {
      // Check for custom submenu from site configuration
      if (link.submenu?.rendered) {
        return {
          ...link,
          submenu: {
            ...link.submenu,
            rendered: link.submenu.rendered
          }
        }
      }
      return link
    })
  }
  
  return links
})

// Process submenu HTML to convert relative links
const processedSubmenu = (html: string): string => {
  if (!html) return ''
  if (typeof document === 'undefined') return html

  // Create a temporary DOM element to parse the HTML
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = html

  // Process all links in the temporary DOM
  const links = tempDiv.querySelectorAll('a')
  links.forEach(link => {
    const href = link.getAttribute('href')
    
    // Check if it's a relative link (doesn't start with http, https, //, #, tel:, mailto:)
    if (href && !href.match(/^(https?:\/\/|\/\/|#|tel:|mailto:)/)) {
      // Add a special data attribute to identify this as a relative link
      link.setAttribute('data-nuxt-link', 'true')
      link.setAttribute('data-href', href)
      
      // Remove the href attribute to prevent default navigation
      link.removeAttribute('href')
    }
  })

  // Apply class transformations
  const classMap: Record<string, string> = {
    'uk-grid': 'grid grid-cols-1 md:grid-cols-3 gap-4',
    'uk-flex': 'flex',
    'uk-text-meta': 'text-xs',
    'uk-flex-middle': 'items-center',
    'uk-width-1-1': 'w-full',
    'uk-width-1-3@l': 'w-full',
    'uk-padding-small': 'p-4',
    'uk-text-center': 'text-center',
    'uk-link-reset': 'no-underline text-foreground hover:text-primary transition-colors duration-200',
    'uk-transition-scale-up uk-transition-opaque': 'transform transition-transform hover:scale-105 duration-200',
    'uk-margin-remove': 'm-0',
    'uk-text-meta uk-text-muted': 'text-sm text-muted-foreground',
    'moon-icon': 'inline-block w-8 h-8 mb-3',
    'icon-nav-title': 'text-lg font-semibold mb-2',
    'icon-sub-text': 'text-sm text-muted-foreground'
  }

  // Apply class transformations by iterating through elements with specific classes
  const allElements = tempDiv.querySelectorAll('*')
  allElements.forEach(el => {
    if (el.classList) {
      const classesToRemove: string[] = []
      const classesToAdd: string[] = []
      
      // Check each class on the element
      el.classList.forEach(className => {
        if (classMap[className]) {
          classesToRemove.push(className)
          classesToAdd.push(...classMap[className].split(' '))
        }
      })
      
      // Remove old classes
      classesToRemove.forEach(cls => el.classList.remove(cls))
      
      // Add new classes
      classesToAdd.forEach(cls => el.classList.add(cls))
    }
  })

  return tempDiv.innerHTML
}

// Handle clicks on mobile submenu items
const handleMobileSubmenuClick = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  const link = target.closest('[data-nuxt-link="true"]')
  
  if (link) {
    event.preventDefault()
    const href = link.getAttribute('data-href')
    if (href) {
      // Close the mobile menu before navigation
      mobileOpen.value = false
      // Use Nuxt's router to navigate
      navigateTo(href)
    }
  }
}

// Cart actions

// Use cart store actions
const toggleCart = () => {
  cartStore.toggleCart();
}

const closeCart = () => {
  cartStore.closeCart();
}

// Mobile menu state is defined but not currently used
// This can be implemented in the future if needed

// Initialize data
onMounted(async () => {
  try {
    // Wait for site store to be ready
    if (typeof siteStore.initialize === 'function') {
      await siteStore.initialize()
    }
    isLoading.value = false
  } catch (error) {
    console.error('[AppHeader] Error initializing:', error)
    isLoading.value = false
  }

  if (process.client) {
    // Use event delegation to handle clicks on relative links
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement
      const link = target.closest('[data-nuxt-link="true"]')
      
      if (link) {
        event.preventDefault()
        const href = link.getAttribute('data-href')
        if (href) {
          // Close mobile menu and navigate
          mobileOpen.value = false
          navigateTo(href)
        }
      }
    })
  }
})

onBeforeUnmount(() => {
  // Event listeners will be cleaned up automatically
})
</script>

<style>
/* Mobile submenu styling */
.mobile-submenu-content {
  padding-top: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.mobile-submenu-content .grid {
  display: flex !important;
  flex-direction: column !important;
  gap: 1rem;
}

.mobile-submenu-content .grid > div {
  margin-bottom: 0.75rem;
}

.mobile-submenu-content h3 {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: var(--foreground);
}

.mobile-submenu-content [data-nuxt-link="true"] {
  display: block;
  padding: 0.625rem 0.75rem;
  border-radius: 0.375rem;
  color: rgba(var(--foreground-rgb), 0.8);
  transition-property: color, background-color;
  transition-duration: 200ms;
  cursor: pointer;
  font-size: 1rem;
}

.mobile-submenu-content [data-nuxt-link="true"]:hover {
  color: var(--primary);
  background-color: rgba(var(--accent-rgb), 0.3);
}

/* Mobile nav item hover/active states */
.mobile-nav-item a {
  transition-property: color, background-color;
  transition-duration: 200ms;
}

.mobile-nav-item a:hover {
  background-color: rgba(var(--accent-rgb), 0.3);
}

.mobile-nav-item a:active {
  background-color: rgba(var(--accent-rgb), 0.5);
}

.mobile-nav-item .hover\:text-primary:hover {
  color: var(--primary);
}

/* Disclosure animations are handled by HeadlessUI */

/* Enhanced scrolling for mobile sheet */
:deep(.sheet-scrollable) {
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  scroll-behavior: smooth;
}

/* Ensure sheet content scrolls properly on mobile */
:deep([data-radix-dialog-content]) {
  touch-action: pan-y;
  max-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* Specific mobile sheet styling */
.mobile-sheet-content {
  height: 100vh;
  max-height: 100vh;
  overflow: hidden;
}

.mobile-sheet-content .sheet-scrollable {
  flex: 1;
  min-height: 0;
}
</style>
